import { ReadonlyVec2, vec2 } from 'gl-matrix';
import { enqueueSnackbar } from 'notistack';
import {
    GizmoGeometry,
    TransformGizmo,
} from '~/engine/data/common/customs/TransformGizmo';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { View } from '~/engine/view/View';
import { PlaneView, PlaneViewType } from '~/engine/view/views/PlaneView';
import { StructureRotateAction } from '../../actions/transform-actions/StructureRotateAction';
import { StructureScaleAction } from '../../actions/transform-actions/StructureScaleAction';
import { StructureTranslateAction } from '../../actions/transform-actions/StructureTranslateAction';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { CrosshairInteractionMode, Tool } from '../../Tool';
import { ToolAction } from '../../ToolAction';

type HitZone =
    | 'inside'
    | 'corner'
    | 'edge-x'
    | 'edge-y'
    | 'circle-boundary'
    | 'outside';

/** Pixel tolerance for clicking on the circle outline. */
const CIRCLE_HIT_TOLERANCE = 8;

/** Pixel tolerance for clicking on corner handles. */
const CORNER_HIT_TOLERANCE = 10;

/** Pixel tolerance for clicking on edge midpoint indicators. */
const EDGE_HIT_TOLERANCE = 10;

const NOT_HANDLED = new EventHandlerResult(
    EventHandlerResultType.EVENT_NOT_HANDLED
);

/**
 * Custom CSS cursor for rotation — a circular arrow SVG encoded
 * as a data URI. The hotspot is at the centre of the 24x24 icon.
 */
const ROTATE_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"' +
        ' fill="none" stroke="white" stroke-width="2" stroke-linecap="round"' +
        ' stroke-linejoin="round">' +
        '<path d="M21 12a9 9 0 1 1-9-9c2.5 0 4.8 1 6.5 2.6"/>' +
        '<polyline points="21 3 21 9 15 9"/>' +
        '</svg>'
)}") 12 12, grab`;

/**
 * Interactive tool for translating, scaling, and rotating a
 * selected structure.
 *
 * A bounding-box gizmo (rectangle + circumscribed circle +
 * corner handles + edge midpoint indicators) is drawn around
 * the structure while the tool is active.
 *
 * - **Corner handles** activate uniform 3D scaling.
 * - **Edge midpoints** activate single-axis scaling
 *   (left/right = view-X, top/bottom = view-Y).
 * - **Circle outline** activates rotation around the view normal.
 * - **Inside the box** activates translation.
 */
export class TransformTool extends Tool {
    public readonly toolType = 'transform';

    private readonly _roi: RegionOfInterest;
    private readonly _views: readonly PlaneView[];
    private readonly _siblingSupports: readonly RegionOfInterest[];

    private _activeAction?: ToolAction;
    private _lastCursor = '';

    /** Gizmos keyed by view — one per PlaneView. */
    private readonly _gizmos = new Map<PlaneView, TransformGizmo>();

    constructor(
        roi: RegionOfInterest,
        views: readonly PlaneView[],
        siblingSupports?: RegionOfInterest[]
    ) {
        super();
        this._roi = roi;
        this._views = views;
        this._siblingSupports = siblingSupports ?? [];
        this.addGizmosToAllViews();
    }

    public override isActionInProgress(): boolean {
        return this._activeAction !== undefined;
    }

    public override getCrosshairInteractionMode(
        _isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.gizmoCrosshairMode(this.isStrokeInProgress());
    }

    public onActivate(): EventHandlerResult {
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            false,
            new VolumeViewUpdateEvent(
                undefined,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    public onDeactivate(): EventHandlerResult {
        this.resetCursor();
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            false,
            new VolumeViewUpdateEvent(
                undefined,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    public dispose(): void {
        this.removeAllGizmos();
        // Re-composite so the just-removed gizmos disappear immediately.
        for (const view of this._views) {
            view.renderLayers();
        }
    }

    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: View
    ): EventHandlerResult {
        if (
            button !== 0 ||
            !(view instanceof PlaneView) ||
            view.type !== PlaneViewType.Primary
        ) {
            return NOT_HANDLED;
        }

        // Guard: do not modify approved/reviewed structures
        if (this._roi.canModify === false) {
            enqueueSnackbar(
                'Transform is not allowed for this ROI' +
                    ' as it has already been reviewed.',
                { variant: 'warning' }
            );
            return NOT_HANDLED;
        }

        const texture = this._roi.texture;
        if (!texture || texture.isEmpty) {
            return NOT_HANDLED;
        }

        const zone = this.hitTest(view, coordinates);

        if (zone === 'outside') {
            return NOT_HANDLED;
        }

        this._activeAction = this.createAction(zone, view);

        return this._activeAction.onMousePress(
            button,
            modifierKeys,
            coordinates,
            view
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: View
    ): EventHandlerResult {
        if (this._activeAction) {
            return this._activeAction.onMouseMove(coordinates, buttons, view);
        }

        if (view instanceof PlaneView) {
            this.updateCursor(view, coordinates);
        }

        return NOT_HANDLED;
    }

    public onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2,
        view: View
    ): EventHandlerResult {
        if (this._activeAction) {
            if (view instanceof PlaneView) {
                this.setCursor(view, 'default');
            }
            const result = this._activeAction.onMouseRelease(
                button,
                coordinates,
                view
            );
            this._activeAction = undefined;
            return result;
        }

        return NOT_HANDLED;
    }

    // ── Action factory ────────────────────────────────────────────

    private createAction(zone: HitZone, view: PlaneView): ToolAction {
        switch (zone) {
            case 'inside':
                return new StructureTranslateAction(
                    this._roi,
                    this._siblingSupports
                );
            case 'corner':
                return new StructureScaleAction(this._roi, { type: 'uniform' });
            case 'edge-x':
                return new StructureScaleAction(this._roi, {
                    type: 'single-axis',
                    axis: view.viewXDirection,
                    canvasAxis: 'x',
                });
            case 'edge-y':
                return new StructureScaleAction(this._roi, {
                    type: 'single-axis',
                    axis: view.viewYDirection,
                    canvasAxis: 'y',
                });
            case 'circle-boundary':
                return new StructureRotateAction(this._roi);
            default:
                throw new Error(`Unexpected hit zone: ${zone}`);
        }
    }

    // ── Gizmo management ──────────────────────────────────────────

    private addGizmosToAllViews(): void {
        const texture = this._roi.texture;
        if (!texture || texture.isEmpty) {
            return;
        }

        for (const view of this._views) {
            if (!this._gizmos.has(view)) {
                const gizmo = new TransformGizmo(this._roi);
                this._gizmos.set(view, gizmo);
                view.addCustomObject(gizmo);
            }
        }
    }

    private removeAllGizmos(): void {
        for (const [view, gizmo] of this._gizmos) {
            view.removeCustomObject(gizmo);
        }
        this._gizmos.clear();
    }

    // ── Hit testing ───────────────────────────────────────────────

    /**
     * Determine the gizmo zone under the mouse position.
     *
     * Priority order (most specific first):
     * 1. Corner handles  → uniform 3D scale
     * 2. Edge midpoints  → single-axis scale
     * 3. Circle boundary → rotation
     * 4. Inside box      → translation
     * 5. Outside         → nothing
     */
    private hitTest(view: PlaneView, coordinates: ReadonlyVec2): HitZone {
        const gizmo = this._gizmos.get(view);
        if (!gizmo) {
            return 'outside';
        }

        const geometry = gizmo.getGizmoGeometry(view);
        if (!geometry) {
            return 'outside';
        }

        const canvasCoords = view.clientToCanvas(coordinates);
        const mouseX = canvasCoords[0];
        const mouseY = canvasCoords[1];

        // 1. Corner handles (highest priority)
        if (this.isNearCorner(geometry, mouseX, mouseY)) {
            return 'corner';
        }

        // 2. Edge midpoints
        const edgeZone = this.isNearEdgeMidpoint(geometry, mouseX, mouseY);
        if (edgeZone) {
            return edgeZone;
        }

        // 3. Circle boundary
        const dist = vec2.distance(
            canvasCoords as vec2,
            geometry.center as vec2
        );
        if (Math.abs(dist - geometry.radius) < CIRCLE_HIT_TOLERANCE) {
            return 'circle-boundary';
        }

        // 4. Inside bounding box (rectangle, not circle)
        const cx = geometry.center[0];
        const cy = geometry.center[1];
        const hx = geometry.halfSize[0];
        const hy = geometry.halfSize[1];
        if (Math.abs(mouseX - cx) < hx && Math.abs(mouseY - cy) < hy) {
            return 'inside';
        }

        return 'outside';
    }

    private isNearCorner(
        geometry: GizmoGeometry,
        mouseX: number,
        mouseY: number
    ): boolean {
        for (const corner of geometry.corners) {
            const dx = mouseX - corner[0];
            const dy = mouseY - corner[1];
            if (
                dx * dx + dy * dy <
                CORNER_HIT_TOLERANCE * CORNER_HIT_TOLERANCE
            ) {
                return true;
            }
        }
        return false;
    }

    private isNearEdgeMidpoint(
        geometry: GizmoGeometry,
        mouseX: number,
        mouseY: number
    ): 'edge-x' | 'edge-y' | null {
        const mp = geometry.edgeMidpoints;
        const tolSq = EDGE_HIT_TOLERANCE * EDGE_HIT_TOLERANCE;

        // Left and right midpoints → scale in view-X direction
        for (const pt of [mp.left, mp.right]) {
            const dx = mouseX - pt[0];
            const dy = mouseY - pt[1];
            if (dx * dx + dy * dy < tolSq) {
                return 'edge-x';
            }
        }

        // Top and bottom midpoints → scale in view-Y direction
        for (const pt of [mp.top, mp.bottom]) {
            const dx = mouseX - pt[0];
            const dy = mouseY - pt[1];
            if (dx * dx + dy * dy < tolSq) {
                return 'edge-y';
            }
        }

        return null;
    }

    // ── Cursor ────────────────────────────────────────────────────

    private static readonly CURSOR_MAP: Record<HitZone, string> = {
        inside: 'move',
        corner: 'nwse-resize',
        'edge-x': 'ew-resize',
        'edge-y': 'ns-resize',
        'circle-boundary': ROTATE_CURSOR,
        outside: 'default',
    };

    private updateCursor(view: PlaneView, coordinates: ReadonlyVec2): void {
        const zone = this.hitTest(view, coordinates);
        this.setCursor(view, TransformTool.CURSOR_MAP[zone]);
    }

    private setCursor(view: PlaneView, cursor: string): void {
        if (this._lastCursor !== cursor && view.canvas) {
            view.canvas.style.cursor = cursor;
            this._lastCursor = cursor;
        }
    }

    private resetCursor(): void {
        for (const view of this._views) {
            if (view.canvas) {
                view.canvas.style.cursor = 'default';
            }
        }
        this._lastCursor = '';
    }
}
